import { useState, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { queryKeys } from '@/shared/hooks/queries/queryKeys'
import { getDrugs } from '@/features/internal/drugs/apis/soapTemplate'
import { Drug } from '@/features/internal/drugs/types/soapTemplate'

interface UseDrugAutocompleteQueryOptions {
  /**
   * 検索を有効にする最小文字数
   * @default 2
   */
  minChars?: number

  /**
   * debounce 遅延時間（ミリ秒）
   * @default 300
   */
  debounceMs?: number

  /**
   * キャッシュの有効期間（ミリ秒）
   * @default 300000 (5分)
   */
  staleTime?: number
}

interface UseDrugAutocompleteQueryReturn {
  /**
   * 検索結果の医薬品リスト
   */
  options: Drug[]

  /**
   * ローディング状態
   */
  isLoading: boolean

  /**
   * debounce 適用後の検索キーワード
   */
  debouncedSearchTerm: string
}

/**
 * 医薬品オートコンプリート用クエリフック
 *
 * 責務:
 * - debounce 処理の内蔵
 * - 最小文字数チェック
 * - TanStack Query によるキャッシュ管理
 *
 * 使用例:
 * ```tsx
 * const [inputValue, setInputValue] = useState('')
 * const { options, isLoading } = useDrugAutocompleteQuery(inputValue)
 * ```
 */
export const useDrugAutocompleteQuery = (
  searchTerm: string,
  options: UseDrugAutocompleteQueryOptions = {}
): UseDrugAutocompleteQueryReturn => {
  const { minChars = 2, debounceMs = 300, staleTime = 1000 * 60 * 5 } = options

  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('')

  // debounce 処理
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchTerm.length >= minChars) {
        setDebouncedSearchTerm(searchTerm)
      } else {
        setDebouncedSearchTerm('')
      }
    }, debounceMs)

    return () => clearTimeout(timer)
  }, [searchTerm, minChars, debounceMs])

  // TanStack Query
  const query = useQuery({
    queryKey: queryKeys.drugs.list({ page: 1, search: debouncedSearchTerm }),
    queryFn: async () => {
      const response = await getDrugs(1, debouncedSearchTerm)
      return response.data.results ?? []
    },
    enabled: debouncedSearchTerm.length >= minChars,
    staleTime,
  })

  return {
    options: query.data ?? [],
    isLoading: query.isLoading && debouncedSearchTerm.length >= minChars,
    debouncedSearchTerm,
  }
}
